import React, { useEffect, useState, useCallback } from 'react';
import {
  PlusIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  ArrowPathIcon,
  LinkIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { WorkspacePanel } from './OrbexAccounts';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function StatusBadge({ active, revokedAt }) {
  if (revokedAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
        <XMarkIcon className="w-3 h-3" /> Revoked
      </span>
    );
  }
  return active ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
      <CheckIcon className="w-3 h-3" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-500">
      Inactive
    </span>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="Copy"
      className="ml-1 p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
    >
      {copied ? <CheckIcon className="w-3.5 h-3.5 text-emerald-500" /> : <ClipboardDocumentIcon className="w-3.5 h-3.5" />}
    </button>
  );
}

// ── Create license modal ──────────────────────────────────────────────────────

function CreateModal({ onClose, onCreate }) {
  const [label, setLabel]           = useState('');
  const [maxProfiles, setMaxProfiles] = useState('');
  const [expiresAt, setExpiresAt]   = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const body = {
        label: label.trim() || undefined,
        maxProfiles: maxProfiles ? Number(maxProfiles) : undefined,
        expiresAt: expiresAt || undefined,
      };
      const res = await api.post('/admin/orbex/licenses', body);
      if (res.data?.success) {
        onCreate(res.data.license);
        onClose();
      } else {
        setError(res.data?.error || 'Unknown error');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold">Create Orbex License</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Label <span className="text-gray-400">(e.g. EPYC-1 VPS)</span>
            </label>
            <input
              className="input w-full"
              placeholder="EPYC-1 VPS"
              value={label}
              onChange={e => setLabel(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Max browser profiles <span className="text-gray-400">(blank = unlimited)</span>
            </label>
            <input
              className="input w-full"
              type="number"
              min="1"
              placeholder="e.g. 50"
              value={maxProfiles}
              onChange={e => setMaxProfiles(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Expires <span className="text-gray-400">(blank = never)</span>
            </label>
            <input
              className="input w-full"
              type="date"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={loading}>
              {loading ? 'Creating…' : 'Create key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Download modal ────────────────────────────────────────────────────────────

function DownloadModal({ license, onClose }) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState('');

  useEffect(() => {
    api.get(`/admin/orbex/licenses/${license.key}/download`)
      .then(r => { if (r.data?.success) setData(r.data); else setErr(r.data?.error); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [license.key]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-gray-900 shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold">Deploy to VPS — {license.label || license.key.slice(0, 18) + '…'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {loading && <p className="text-sm text-gray-400">Generating install command…</p>}
        {err && <p className="text-sm text-red-600">{err}</p>}

        {data && (
          <div className="space-y-4 text-sm">
            {/* Security note */}
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              <ShieldCheckIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Downloads are routed through <strong>wingr.io</strong> — the private GitHub repo is never exposed.
                The license key is embedded in the URL. Do not share this command publicly.
              </span>
            </div>

            <p className="text-gray-500 dark:text-gray-400">
              Paste this one-liner into <strong>PowerShell (Admin)</strong> on the VPS.
              It downloads Orbex from wingr.io, installs it, and writes the license key automatically.
            </p>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Install command</span>
                <CopyButton value={data.installCmd} />
              </div>
              <pre className="rounded-lg bg-gray-900 text-green-400 text-xs p-3 overflow-x-auto whitespace-pre-wrap break-all">
                {data.installCmd}
              </pre>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-gray-500">License key</span>
                <div className="flex items-center gap-1 mt-0.5 font-mono font-medium break-all">
                  {license.key} <CopyButton value={license.key} />
                </div>
              </div>
              <div>
                <span className="text-gray-500">Max profiles</span>
                <div className="mt-0.5 font-medium">{data.maxProfiles ?? 'Unlimited'}</div>
              </div>
              <div>
                <span className="text-gray-500">Expires</span>
                <div className="mt-0.5 font-medium">{fmtDate(data.expiresAt)}</div>
              </div>
              <div>
                <span className="text-gray-500">Download route</span>
                <div className="mt-0.5 text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                  <ShieldCheckIcon className="w-3.5 h-3.5" /> via wingr.io (private)
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditModal({ license, onClose, onSave, agents }) {
  const [label, setLabel]           = useState(license.label || '');
  const [maxProfiles, setMaxProfiles] = useState(license.maxProfiles ?? '');
  const [profilesUsed, setProfilesUsed] = useState(license.profilesUsed ?? 0);
  const [agentId, setAgentId]       = useState(license.agentId ? String(license.agentId) : '');
  const [expiresAt, setExpiresAt]   = useState(
    license.expiresAt ? license.expiresAt.slice(0, 10) : ''
  );
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const body = {
        label:        label.trim(),
        maxProfiles:  maxProfiles !== '' ? Number(maxProfiles) : null,
        profilesUsed: Number(profilesUsed) || 0,
        agentId:      agentId || null,
        expiresAt:    expiresAt || null,
      };
      const res = await api.patch(`/admin/orbex/licenses/${license.key}`, body);
      if (res.data?.success) { onSave(res.data.license); onClose(); }
      else setError(res.data?.error || 'Unknown error');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold">Edit License</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Label</label>
            <input className="input w-full" value={label} onChange={e => setLabel(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Profiles used
              </label>
              <input
                className="input w-full" type="number" min="0"
                value={profilesUsed} onChange={e => setProfilesUsed(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Max profiles <span className="text-gray-400">(blank = ∞)</span>
              </label>
              <input
                className="input w-full" type="number" min="1"
                value={maxProfiles} onChange={e => setMaxProfiles(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Bill imports on VPS
            </label>
            <select
              className="input w-full"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
            >
              <option value="">Match by label (e.g. Epyc - 2)</option>
              {(agents || []).map((a) => (
                <option key={a._id} value={a._id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Expires <span className="text-gray-400">(blank = never)</span>
            </label>
            <input className="input w-full" type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" className="btn-primary flex-1" disabled={loading}>
              {loading ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function OrbexLicenses() {
  const { user } = useAuth();
  const [licenses, setLicenses]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [downloadLic, setDownloadLic] = useState(null);
  const [editLic, setEditLic]       = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [agents, setAgents]         = useState([]);

  const isSuperAdmin = user?.role === 'superadmin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [licRes, agentRes] = await Promise.all([
        api.get('/admin/orbex/licenses'),
        api.get('/admin/va/ai-agents', { params: { compact: 1 } }).catch(() => null)
      ]);
      setLicenses(licRes.data?.licenses || []);
      const rows = agentRes?.data?.agents || agentRes?.data?.items || [];
      setAgents(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) load();
  }, [load, isSuperAdmin]);

  // Superadmin gate — AFTER all hooks
  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center gap-3">
        <ShieldCheckIcon className="w-12 h-12 text-gray-300" />
        <p className="text-sm text-gray-500">Super-admin access required.</p>
      </div>
    );
  }

  const revoke = async (key) => {
    if (!window.confirm('Revoke this license key? The browser will stop working on its next validation.')) return;
    setActionLoading(p => ({ ...p, [key + '_revoke']: true }));
    try {
      await api.delete(`/admin/orbex/licenses/${key}`);
      setLicenses(prev => prev.map(l => l.key === key ? { ...l, active: false, revokedAt: new Date().toISOString() } : l));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(p => ({ ...p, [key + '_revoke']: false }));
    }
  };

  const bumpUsed = async (lic, delta) => {
    const next = Math.max(0, (Number(lic.profilesUsed) || 0) + delta);
    if (lic.maxProfiles != null && next > Number(lic.maxProfiles)) return;
    setActionLoading((p) => ({ ...p, [lic.key + '_used']: true }));
    try {
      const res = await api.patch(`/admin/orbex/licenses/${lic.key}`, { profilesUsed: next });
      if (res.data?.license) {
        setLicenses((prev) => prev.map((l) => (l.key === lic.key ? res.data.license : l)));
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setActionLoading((p) => ({ ...p, [lic.key + '_used']: false }));
    }
  };

  const unbind = async (key) => {
    if (!window.confirm('Remove machine binding? The key can then be activated on a new machine.')) return;
    setActionLoading(p => ({ ...p, [key + '_unbind']: true }));
    try {
      await api.post(`/admin/orbex/licenses/${key}/unbind`);
      setLicenses(prev => prev.map(l => l.key === key ? { ...l, machineId: null, hostname: '' } : l));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(p => ({ ...p, [key + '_unbind']: false }));
    }
  };

  const activeCount  = licenses.filter(l => l.active && !l.revokedAt).length;
  const revokedCount = licenses.filter(l => l.revokedAt).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Orbex Browser Licenses</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage license keys for internal Orbex browser deployments.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="btn-secondary !py-1.5 !px-3 flex items-center gap-1.5 text-sm"
            disabled={loading}
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="btn-primary !py-1.5 !px-3 flex items-center gap-1.5 text-sm"
          >
            <PlusIcon className="w-4 h-4" />
            New license
          </button>
        </div>
      </div>

      {user?.username === 'superadmin_wingr' && <WorkspacePanel />}

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total keys',  value: licenses.length },
          { label: 'Active',      value: activeCount },
          { label: 'Revoked',     value: revokedCount },
        ].map(c => (
          <div key={c.label} className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-4">
            <div className="text-xs uppercase tracking-wide text-gray-500">{c.label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 text-sm font-semibold flex items-center gap-2">
          <ComputerDesktopIcon className="w-4 h-4 text-orange-500" />
          License Keys
        </div>

        {loading && !licenses.length ? (
          <div className="px-5 py-10 text-sm text-gray-400 text-center">Loading…</div>
        ) : !licenses.length ? (
          <div className="px-5 py-10 text-sm text-gray-400 text-center">
            No license keys yet —{' '}
            <button onClick={() => setShowCreate(true)} className="text-indigo-600 dark:text-indigo-400 hover:underline">
              create the first one
            </button>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Label</th>
                  <th className="px-4 py-2 text-left font-medium">Key</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Machine</th>
                  <th className="px-4 py-2 text-left font-medium">Profiles</th>
                  <th className="px-4 py-2 text-left font-medium">Expires</th>
                  <th className="px-4 py-2 text-left font-medium">Created</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {licenses.map((lic) => {
                  const profiles = lic.maxProfiles != null
                    ? `${lic.profilesUsed ?? 0} / ${lic.maxProfiles}`
                    : `${lic.profilesUsed ?? 0} / ∞`;

                  return (
                    <tr key={lic.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                      {/* Label */}
                      <td className="px-4 py-3 font-medium max-w-[160px] truncate">
                        {lic.label || <span className="text-gray-400 italic">no label</span>}
                      </td>

                      {/* Key */}
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
                          {lic.key.slice(0, 22)}…
                        </span>
                        <CopyButton value={lic.key} />
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge active={lic.active} revokedAt={lic.revokedAt} />
                      </td>

                      {/* Machine */}
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[130px] truncate">
                        {lic.hostname || lic.machineId
                          ? <span title={lic.machineId}>{lic.hostname || lic.machineId?.slice(0, 12) + '…'}</span>
                          : <span className="text-gray-300 dark:text-gray-600">unbound</span>
                        }
                      </td>

                      {/* Profiles */}
                      <td className="px-4 py-3 tabular-nums text-xs font-medium">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            title="Lower used count"
                            disabled={!!actionLoading[lic.key + '_used'] || (lic.profilesUsed || 0) <= 0}
                            onClick={() => bumpUsed(lic, -1)}
                            className="w-5 h-5 rounded border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                          >
                            −
                          </button>
                          <span>{profiles}</span>
                          <button
                            type="button"
                            title="Raise used count"
                            disabled={!!actionLoading[lic.key + '_used'] || (lic.maxProfiles != null && (lic.profilesUsed || 0) >= lic.maxProfiles)}
                            onClick={() => bumpUsed(lic, 1)}
                            className="w-5 h-5 rounded border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      {/* Expires */}
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {fmtDate(lic.expiresAt)}
                      </td>

                      {/* Created */}
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {fmtDate(lic.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {/* Download / deploy */}
                          {lic.active && !lic.revokedAt && (
                            <button
                              type="button"
                              title="Get install command"
                              onClick={() => setDownloadLic(lic)}
                              className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              <LinkIcon className="w-3.5 h-3.5" />
                              Deploy
                            </button>
                          )}

                          {/* Edit */}
                          {!lic.revokedAt && (
                            <button
                              type="button"
                              title="Edit"
                              onClick={() => setEditLic(lic)}
                              className="text-xs font-medium text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                            >
                              Edit
                            </button>
                          )}

                          {/* Unbind machine */}
                          {lic.machineId && !lic.revokedAt && (
                            <button
                              type="button"
                              title="Unbind machine"
                              onClick={() => unbind(lic.key)}
                              disabled={actionLoading[lic.key + '_unbind']}
                              className="text-xs font-medium text-amber-600 hover:underline disabled:opacity-50"
                            >
                              Unbind
                            </button>
                          )}

                          {/* Revoke */}
                          {!lic.revokedAt && (
                            <button
                              type="button"
                              title="Revoke key"
                              onClick={() => revoke(lic.key)}
                              disabled={actionLoading[lic.key + '_revoke']}
                              className="p-1 rounded text-red-400 hover:text-red-600 disabled:opacity-40"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreate={(lic) => setLicenses(prev => [lic, ...prev])}
        />
      )}
      {downloadLic && (
        <DownloadModal
          license={downloadLic}
          onClose={() => setDownloadLic(null)}
        />
      )}
      {editLic && (
        <EditModal
          license={editLic}
          agents={agents}
          onClose={() => setEditLic(null)}
          onSave={(updated) => {
            setLicenses(prev => prev.map(l => l.key === updated.key ? updated : l));
          }}
        />
      )}
    </div>
  );
}
